import React, { useState } from 'react';
import { UserGroupIcon, UserIcon, HomeIcon, HeartIcon, PhoneIcon, EnvelopeIcon, ClipboardDocumentCheckIcon, BeakerIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { StatusBadge } from '../../../shared/components/badges/StatusBadge';
import AddChildModal from '../../../shared/components/ui/modals/AddChildModal';
import ChildExpandRow from '../components/ChildExpandRow';
import TabbedForm from '../../../shared/components/forms/FormTabs';
import { ProfileUpload } from '../../users/components/profileUpload';
import { getParentOptions, PatientService } from '../../../shared/services/patientService';
import { ChildrenService } from '../../../shared/services/childrenService';
import { render } from '@testing-library/react';

// Generic loader for dropdowns from API with fallback
const loadDropdownOptions = async (key, fallback = []) => {
  try {
    const res = await ChildrenService.getDropdowns();

    const raw =
      res?.data?.data?.[key] ||
      res?.data?.[key] ||
      res?.[key] ||
      [];

    return raw.map(item => ({
      value: item.value,
      label: item.label
    }));
  } catch (error) {
    console.warn(`Failed to load ${key} from API. Using fallback`, error);
    return fallback;
  }
};

// Options loaders
export const getGenderOptions = () => loadDropdownOptions('genders');
export const getBloodGroupOptions = () => loadDropdownOptions('bloodGroups');
export const getRelationOptions = () => loadDropdownOptions('relations');
export const getMedicalConditionsOptions = () => loadDropdownOptions('medicalConditions');
// export const getAllergiesOptions = () => loadDropdownOptions('allergies');

export const childRecordConfig = {
  title: 'Child Records',
  endpoint: 'admin/children',
  storageKey: 'childrenRecords',
  icon: UserGroupIcon,
  useUrlNavigation: true,
  basePath: '/admin/children',

  columns: [
    {
      key: 'childName',
      label: 'Name',
      render: (item) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 h-10 w-10">
            {item.profilePhoto ? (
              <img 
                src={item.profilePhoto} 
                alt={item.name} 
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center">
                <span className="text-lg">👨‍👩‍👧‍👦</span>
              </div>
            )}
          </div>
          <div>
           {item.name}
          </div>
        </div>
      )
    },
    {
      key: 'parentName',
      label: 'Parent/Guardian Name',
      render: (item) => item.parent.fullName
    },
        {
      key: 'relationWithChild',
      label: 'Relation',
      render: (item) => 
   
          item.relationWithChild || '—'
    },
       {
      key: 'birthDate',
      label: 'Birth Date',
      render: (item) => (
        <span>
          {new Date(item.dateOfBirth).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })}
        </span>
      )
   
      
    },

    {
      key: 'gender',
      label: 'Gender',
      render: (item) => item.gender
    }
  ],

  formFields: [

 {
    name: "profilePicture",
    label: "Profile Picture",
    accept: 'image/*',
    placeholder: 'Upload parent profile picture',
    section: 'personal',
    fullWidth: true,
    customRender: (field, value, onChange, error) => (
      <ProfileUpload field={field} value={value} onChange={onChange} error={error} />
    ),
    // optional
  },
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Child name',
      section: 'personal'
    },
{
  name: 'parentId',
  label: 'Parent/Guardian Name',
  type: 'select',
  required: true,
  placeholder: 'Enter parent/guardian name',
  section: 'personal',
  optionsLoader: async (values) => {
    const options = await getParentOptions();
    // console.log('📋 Parent options:', options); // Should show [{value: "id", label: "name"}, ...]
    return options;
  },
  // Add these properties to ensure proper value handling
  isMulti: false,
  isClearable: false,
  // Transform the selected option to just the value
  transform: (option) => {
    // console.log('🔄 Transform received:', option);
    // If option is already a string (ID), return it
    if (typeof option === 'string') return option;
    // If it's an object, extract the value
    return option?.value || option;
  },
  renderValue: (value, item) => item?.parent?.fullName || value || '—',
  disabled: () => {
    const pathSegments = window.location.pathname.split('/');
    const addIndex = pathSegments.indexOf('add');
    return addIndex !== -1 && pathSegments[addIndex + 1];
  }
},
    {
      name: 'dateOfBirth',
      label: 'Date of Birth',
      type: 'date',
      required: true,
      placeholder: 'Enter Date of Birth',
      section: 'personal',
      renderValue: (value, item) => new Date(item.dateOfBirth).toISOString().split('T')[0] || value || '—'
    },
    {
      name: 'gender',
      label: 'Gender',
      type: 'select',
      required: true,
      section: 'personal',
      optionsLoader: getGenderOptions
    },
    {
      name: 'bloodGroup',
      label: 'Blood Group',
      type: 'select',
      section: 'personal',
      optionsLoader: getBloodGroupOptions
    },
    {
      name: 'weightKg',
      label: 'Weight (in kg)',
      type: 'number',
      required: true,
      placeholder: 'Enter weight of child',
      section: 'personal'
    },
    {
      name: 'heightCm',
      label: 'Height (in cm)',
      type: 'number',
      required: true,
      placeholder: 'Enter height of child',
      section: 'personal'
    },
    {
      name: 'relationWithChild',
      label: 'Relation with Child',
      type: 'select',
      required: true,
      placeholder: 'Select relation',
      section: 'personal',
      optionsLoader: getRelationOptions,
      renderValue: (value, item) => item?.relationWithChild || value || '—'
    },
    {
      name: 'isActive',
      label: 'Is Active',
      type: 'checkbox',
      renderValue: (value, item) => item?.isActive || value || '—',
      section: 'personal',
      // Confirmation modal configuration
      requireConfirmation: (value) => value === false ? true : false , // when child is turned to inactive then show confirmation modal otherwise its not required
      confirmTitle: 'Confirm Status Change',
      confirmMessage: (value) => value === false ?  'Are you sure you want to mark this child as inactive ? This action will classify the child as deceased, and you will no longer be able to access their records. ' : '',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
    },
    // Medical & Notes
    /*{
      name: 'allergies',
      label: 'Allergies',
      type: 'array',// implement formik
      // required: true, 
      section: 'medical'
    },
    {
      name: 'medicalConditions',
      label: 'Medical Conditions',
      type: 'array', // implement formik
      // required: true,
      section: 'medical'
    },*/

        {
      name: 'pediatrician',
      label: 'Pediatrician',
      type: 'string',
      placeholder: 'Pediatrician name...',
      fullWidth: true,
      section: 'medical'
    },
    {
      name: 'allergies',
      label: 'Allergies',
      type: 'multi-append',
      placeholder: 'Any allergies about the child...',
      tooltip: "After typing please press Enter",
      rows: 3,
      fullWidth: true,
      section: 'medical',
      renderValue: (value, item) => (Array.isArray(item.allergies) ? item.allergies.map(allergy=>{
        const firstLetter = allergy.charAt(0).toUpperCase();
        return (firstLetter + allergy.slice(1).toLowerCase()).replace(/_/g, ' ');
      }).join(', ') : value || '—')
    },
    {
      name: 'medicalConditions',
      label: 'Medical Conditions',
      type: 'multi-select',
      optionsLoader: getMedicalConditionsOptions,
      rows: 3,
      fullWidth: true,
      section: 'medical',
      renderValue: (value, item) => (Array.isArray(item.medicalConditions) ? item.medicalConditions.map(cond => {
        const firstLetter = cond.charAt(0).toUpperCase();
        return (firstLetter + cond.slice(1).toLowerCase()).replace(/_/g, ' ');
      }).join(', ') : value || '—')

    },
    {
      name: 'specialNotes',
      label: 'Special Notes',
      type: 'textarea',
      placeholder: 'Any additional notes about the child...',
      rows: 3,
      fullWidth: true,
      section: 'medical'
    }

  ],

  searchFields: ['parentName', 'email', 'phone', 'relationship'],
  formState: {
    activeTab: 'personal'
  },

  // Custom action buttons
  customActions: [
    {
      icon: <BeakerIcon className="w-5 h-5" />,
      tooltip: 'Vaccination Records',
      className: 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30',
      onClick: (item, openCustomModal, navigate) => {
        navigate(`/admin/children/${item.id}/vaccination-records`);
      }
    },
    {
      icon: <ClipboardIcon className="w-5 h-5" />,
      tooltip: 'Vaccination Plan',
      className: 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30',
      onClick: (item, openCustomModal, navigate) => {
        navigate(`/admin/children/${item.id}/vaccination-plan`);
      }
    }
  ],

customHandlers: {
  renderFormSections: (formFields, formData, handleFormChange, mode, renderFormField, activeTab, setActiveTab) => {
    const tabsConfig = {
      personal: { label: 'Personal Info' },
      // contact: { label: 'Contact & Address' },
      medical: { label: 'Medical & Notes' }
    };
    return <TabbedForm 
      formFields={formFields} 
      formData={formData} 
      handleFormChange={handleFormChange} 
      renderFormField={renderFormField} 
      tabsConfig={tabsConfig}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />;
  },
  
renderExpandedRow: (item, onUpdate) => (
  <ChildExpandRow item={item} onUpdate={onUpdate} viewMode="expanded" />
),
  renderViewDetails: (item) => (
    <ChildExpandRow item={item} viewMode="show" />
  ),
  
  processFormData: (formData, mode) => {
  // console.log('Original formData:', formData);
  // console.log('ParentId being sent:', formData.parentId);
  // console.log('ParentId type:', typeof formData.parentId);
    if(formData?.profilePicture){
      formData.profilePhoto = formData.profilePicture;
      delete formData.profilePicture;
    } 
    const processedData = {
      ...formData,
        parentId: formData.parentId,
      age: parseInt(formData.age) || 0,
      numberOfChildren: parseInt(formData.numberOfChildren, 10) || 0,
      email: formData.email?.toLowerCase(),
      gender: formData.gender?.toUpperCase(),
      bloodGroup: formData.bloodGroup?.toUpperCase().replace(/\+/g, '_POSITIVE').replace(/-/g, '_NEGATIVE'),
      // profilePhoto: formData.profilePicture || ""
    };
    
  
    // Process children array
    if (formData.children && Array.isArray(formData.children)) {
      processedData.children = formData.children.map(child =>
        (
          {
        ...child,
        gender: child.gender?.toUpperCase(),
        bloodGroup: child.bloodGroup?.toUpperCase().replace(/\+/g, '_POSITIVE').replace(/-/g, '_NEGATIVE'),
        allergies: typeof child.allergies === 'string' ? child.allergies.split(',').map(a => a.trim()).filter(Boolean) : (child.allergies || []),
        medicalConditions: typeof child.medicalConditions === 'string' ? child.medicalConditions.split(',').map(m => m.trim()).filter(Boolean) : (child.medicalConditions || []),
        weightKg: parseFloat(child.weightKg) || 0,
        heightCm: parseFloat(child.heightCm) || 0
      }));
    }

    // Combine date and time fields for createdAt
    if (formData.registrationDate && formData.registrationTime) {
      const date = new Date(formData.registrationDate);
      const [hours, minutes] = formData.registrationTime.split(':');
      date.setHours(parseInt(hours), parseInt(minutes));
      processedData.createdAt = date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }) + ', ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }

    // Combine last visit date and time
    if (formData.lastVisitDate && formData.lastVisitTime) {
      const date = new Date(formData.lastVisitDate);
      const [hours, minutes] = formData.lastVisitTime.split(':');
      date.setHours(parseInt(hours), parseInt(minutes));
      processedData.lastVisit = date.toISOString().split('T')[0];
    } else if (formData.lastVisitDate) {
      processedData.lastVisit = formData.lastVisitDate;
    }
  // console.log('Form data AFTER processing:', processedData);
    return processedData;
  },
  
  validateFormData: (formData) => {
    const errors = {};
    
    if (formData.gender && !['MALE', 'FEMALE', 'OTHER'].includes(formData.gender?.toUpperCase())) {
      errors.gender = 'Gender must be MALE, FEMALE, or OTHER';
    }
    
    return errors;
  }
}
};

